/**
 * Bundle Configurator — Custom Web Component
 * -------------------------------------------
 * Works with any Shopify theme. Specifically tested against Hyper (Troop Themes).
 *
 * How it works:
 * 1. Renders N fillable slots + optional fixed gift slot (Liquid does the DOM)
 * 2. Click on a slot opens a picker of products from the source collection
 * 3. Click on a product fills the slot with title, SKU, image
 * 4. Once all slots are filled, the page's Add-to-Cart button is enabled
 * 5. On add-to-cart, we POST a single line item (the bundle variant) with the
 *    customer's selections as line-item properties:
 *      Varianten        = "Original, Strawberry, Raspberry"       (visible)
 *      _Artikelnummern  = "003925, 003991, 002090"                (hidden from customer)
 */

// ----------------------------------------------------------------------------
// Global fetch interceptor (installed once)
// ----------------------------------------------------------------------------
// Last line of defense: if any code on the page tries to POST to /cart/add
// while a bundle-configurator is on the page and incomplete, block the fetch
// at the network layer. This covers any theme JS that uses direct fetch() and
// bypasses DOM events entirely.
if (!window.__bundleConfiguratorFetchPatched) {
  window.__bundleConfiguratorFetchPatched = true;
  const originalFetch = window.fetch;
  window.fetch = function (input, init) {
    const url = typeof input === 'string' ? input : input?.url || '';
    if (url.includes('/cart/add')) {
      const el = document.querySelector('bundle-configurator');
      if (el && el._isComplete === false) {
        if (typeof el.highlightIncomplete === 'function') el.highlightIncomplete();
        return Promise.reject(new Error('Bundle not complete'));
      }
    }
    return originalFetch.apply(this, arguments);
  };
}

class BundleConfigurator extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    // Config from data-attributes (Liquid fills these from metafields)
    this.slotCount = parseInt(this.dataset.slotCount, 10) || 3;
    this.freeSlotEnabled = this.dataset.freeSlotEnabled === 'true';

    // DOM refs
    this.slots = this.querySelectorAll('.bundle-configurator__slot:not([data-slot-gift])');
    this.picker = this.querySelector('.bundle-configurator__picker');
    this.pickerClose = this.querySelector('.bundle-configurator__picker-close');
    this.picks = this.querySelectorAll('.bundle-configurator__pick');
    this.counter = this.querySelector('.bundle-configurator__counter');

    // Variants data — first variant is the bundle's "real" variant
    const variantsEl = document.getElementById('bundleVariantsDataJson');
    this.variants = variantsEl ? JSON.parse(variantsEl.textContent) : [];
    this.bundleVariantId = this.variants[0]?.id || null;

    // Find the page's add-to-cart button (Hyper/Pipeline/Dawn compatible).
    // Prefer button[name="add"] because that's the reliable Shopify convention —
    // Hyper doesn't always set action="/cart/add" on the form in the HTML.
    this.submitBtn =
      document.querySelector('button[name="add"][type="submit"]') ||
      document.querySelector('button[name="add"]') ||
      document.querySelector('[id^="ProductSubmitButton-"]') ||
      document.querySelector('form[action*="/cart/add"] [type="submit"]');
    this.productForm = this.submitBtn?.closest('form') || null;

    // CRITICAL: Clone the submit button to strip all theme-attached listeners.
    // Hyper's <product-form> custom element registers its own click/submit handlers
    // earlier than our custom element connects, so capture-phase alone doesn't win
    // the race. A cloneNode has zero listeners by definition — only our own handler
    // will fire on it.
    if (this.submitBtn) {
      const clone = this.submitBtn.cloneNode(true);
      this.submitBtn.replaceWith(clone);
      this.submitBtn = clone;
    } else {
      console.warn('[BundleConfigurator] Submit button not found on this page.');
    }

    this.currentSlotIndex = null;

    this.bindEvents();
    this.updateSubmitState();

    // Hide placeholder image for empty slots — use CSS default
    this.unpickedImageSrc = this.slots[0]?.querySelector('img')?.src || '';
  }

  bindEvents() {
    // 1. Slot click → open picker
    this.slots.forEach((slot) => {
      slot.addEventListener('click', () => this.openPickerForSlot(slot));
    });

    // 2. Picker close
    this.pickerClose?.addEventListener('click', (e) => {
      e.stopPropagation();
      this.closePicker();
    });

    // 3. Product pick → fill slot
    this.picks.forEach((pick) => {
      pick.addEventListener('click', () => this.fillSlot(pick));
    });

    // 4. Click outside picker closes it
    document.addEventListener('click', (e) => {
      if (!this.contains(e.target)) {
        this.closePicker();
      }
    });

    // 5. Hijack add-to-cart on DOCUMENT level — capture phase fires BEFORE any
    // element-level capture listener in the event path (window → document → ... → button).
    // This is essential because Hyper's <product-form> custom element attaches its own
    // capture-phase listener on itself, which would otherwise win the race.
    const isOurSubmit = (e) => {
      if (!this.submitBtn) return false;
      return e.target === this.submitBtn || this.submitBtn.contains(e.target);
    };

    this._docClickHandler = (e) => {
      if (!isOurSubmit(e)) return;
      const complete = this.getFilledCount() === this.slotCount;
      if (!complete) {
        e.preventDefault();
        e.stopImmediatePropagation();
        this.highlightIncomplete();
        return;
      }
      e.preventDefault();
      e.stopImmediatePropagation();
      this.addBundleToCart();
    };

    this._docSubmitHandler = (e) => {
      if (!this.productForm) return;
      if (e.target !== this.productForm) return;
      const complete = this.getFilledCount() === this.slotCount;
      if (!complete) {
        e.preventDefault();
        e.stopImmediatePropagation();
        this.highlightIncomplete();
        return;
      }
      e.preventDefault();
      e.stopImmediatePropagation();
      this.addBundleToCart();
    };

    // Register at WINDOW level (not document) — window capture fires before document
    // capture in the event propagation path, beating even early-registered theme handlers.
    window.addEventListener('click', this._docClickHandler, { capture: true });
    window.addEventListener('submit', this._docSubmitHandler, { capture: true });
  }

  disconnectedCallback() {
    if (this._docClickHandler) {
      window.removeEventListener('click', this._docClickHandler, { capture: true });
    }
    if (this._docSubmitHandler) {
      window.removeEventListener('submit', this._docSubmitHandler, { capture: true });
    }
  }

  openPickerForSlot(slot) {
    // If slot is already filled, clicking resets it to empty
    if (slot.dataset.checked === 'true') {
      this.clearSlot(slot);
      this.markActiveSlot(slot);
      this.updateSubmitState();
      return;
    }
    this.currentSlotIndex = parseInt(slot.dataset.slotIndex, 10);
    this.markActiveSlot(slot);
    this.picker.setAttribute('data-show', 'true');
    this.setAttribute('data-show', 'true');
  }

  closePicker() {
    this.picker?.setAttribute('data-show', 'false');
    this.setAttribute('data-show', 'false');
    this.clearActiveSlot();
    this.currentSlotIndex = null;
  }

  markActiveSlot(slot) {
    this.clearActiveSlot();
    slot?.classList.add('bundle-configurator__slot--active');
  }

  clearActiveSlot() {
    this.slots.forEach((s) => s.classList.remove('bundle-configurator__slot--active'));
  }

  fillSlot(pick) {
    if (pick.dataset.pickDisabled === 'true') return;
    if (this.currentSlotIndex === null) {
      // Fill first empty slot
      const emptySlot = Array.from(this.slots).find((s) => s.dataset.checked === 'false');
      if (!emptySlot) return;
      this.currentSlotIndex = parseInt(emptySlot.dataset.slotIndex, 10);
    }

    const slot = this.slots[this.currentSlotIndex];
    if (!slot) return;

    const title = pick.dataset.pickTitle;
    const sku = pick.dataset.pickSku || '';
    const imgEl = pick.querySelector('img');

    slot.dataset.checked = 'true';
    slot.dataset.pickTitle = title;
    slot.dataset.pickSku = sku;

    const slotImg = slot.querySelector('img');
    if (slotImg && imgEl) {
      slotImg.src = imgEl.src;
      slotImg.alt = title;
    }
    const slotTitleEl = slot.querySelector('.bundle-configurator__slot-title');
    if (slotTitleEl) slotTitleEl.textContent = title;

    // Visual feedback on the pick
    pick.classList.add('is-picked');
    setTimeout(() => pick.classList.remove('is-picked'), 800);

    // Auto-advance: keep picker open and activate next empty slot, or close if done
    const nextEmpty = Array.from(this.slots).find((s) => s.dataset.checked === 'false');
    if (nextEmpty) {
      this.currentSlotIndex = parseInt(nextEmpty.dataset.slotIndex, 10);
      this.markActiveSlot(nextEmpty);
      // Picker stays open
    } else {
      this.closePicker();
    }
    this.updateSubmitState();
  }

  clearSlot(slot) {
    slot.dataset.checked = 'false';
    delete slot.dataset.pickTitle;
    delete slot.dataset.pickSku;

    const slotImg = slot.querySelector('img');
    if (slotImg && this.unpickedImageSrc) {
      slotImg.src = this.unpickedImageSrc;
      slotImg.alt = '';
    }
    const slotTitleEl = slot.querySelector('.bundle-configurator__slot-title');
    if (slotTitleEl) slotTitleEl.textContent = 'SORTE AUSWÄHLEN';
  }

  getFilledCount() {
    return Array.from(this.slots).filter((s) => s.dataset.checked === 'true').length;
  }

  updateSubmitState() {
    const filled = this.getFilledCount();
    if (this.counter) {
      this.counter.textContent = filled;
      this.counter.dataset.quantity = filled;
    }
    const complete = filled === this.slotCount;
    this._isComplete = complete;  // shared flag used by fetch interceptor

    if (this.submitBtn) {
      this.submitBtn.setAttribute('aria-disabled', complete ? 'false' : 'true');
      this.submitBtn.disabled = !complete;
      // SWITCH TYPE: a type="button" does NOT trigger form submit on click.
      // This alone kills Hyper's form-submit listener when incomplete.
      this.submitBtn.type = complete ? 'submit' : 'button';

      // Inline styles guarantee override of Hyper's theme button styles
      if (complete) {
        this.submitBtn.classList.remove('bundle-configurator__submit--disabled');
        this.submitBtn.style.opacity = '';
        this.submitBtn.style.cursor = '';
        this.submitBtn.style.filter = '';
      } else {
        this.submitBtn.classList.add('bundle-configurator__submit--disabled');
        this.submitBtn.style.opacity = '0.4';
        this.submitBtn.style.cursor = 'not-allowed';
        this.submitBtn.style.filter = 'grayscale(50%)';
      }
    }
    // Gift slot: unlock (full opacity) only when all regular slots are filled
    const giftSlot = this.querySelector('.bundle-configurator__slot--gift');
    if (giftSlot) {
      giftSlot.classList.toggle('is-unlocked', complete);
    }
  }

  highlightIncomplete() {
    // Scroll the configurator into view so user sees the shake feedback
    this.scrollIntoView({ behavior: 'smooth', block: 'center' });
    this.slots.forEach((s) => {
      if (s.dataset.checked === 'false') {
        s.classList.add('bundle-configurator__slot--error');
        setTimeout(() => s.classList.remove('bundle-configurator__slot--error'), 1000);
      }
    });
  }

  async addBundleToCart() {
    if (!this.bundleVariantId) {
      console.error('[BundleConfigurator] No bundle variant id available.');
      return;
    }

    const titles = [];
    const skus = [];
    this.slots.forEach((s) => {
      if (s.dataset.checked === 'true') {
        titles.push(s.dataset.pickTitle);
        if (s.dataset.pickSku) skus.push(s.dataset.pickSku);
      }
    });

    const body = {
      items: [
        {
          id: this.bundleVariantId,
          quantity: 1,
          properties: {
            Varianten: titles.join(', '),
            _Artikelnummern: skus.join(', '),
          },
        },
      ],
    };

    this.submitBtn?.classList.add('is-loading');

    try {
      const res = await fetch(`${window.Shopify.routes.root}cart/add.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      console.log('[BundleConfigurator] Added:', data);

      // Belt-and-suspenders: also write the bundle selection into the cart note.
      // Line-item properties can get stripped by cart-manipulating apps (Pfand, etc.)
      // or hidden by themes. The order note is always visible in Shopify Admin.
      await this.setCartNote(titles, skus);

      // Try to open the theme's cart drawer (Hyper/Pipeline/Dawn compatible)
      this.openCartDrawer();

      // Fire generic Shopify cart-update event
      document.dispatchEvent(new CustomEvent('cart:refresh'));
      document.dispatchEvent(new CustomEvent('cart:added', { detail: data }));
    } catch (err) {
      console.error('[BundleConfigurator] Add-to-cart failed:', err);
      alert('Leider konnte das Bundle nicht hinzugefügt werden. Bitte erneut versuchen.');
    } finally {
      this.submitBtn?.classList.remove('is-loading');
    }
  }

  /**
   * Writes the bundle selection into the cart note so it's guaranteed to
   * appear in the Shopify Admin order view (visible box under "Notes"),
   * order confirmation email, and packing slip — regardless of theme or
   * cart-manipulating apps that might strip line-item properties.
   *
   * Merges with any existing cart note so we don't clobber a second bundle
   * in the same cart or a customer-typed note.
   */
  async setCartNote(titles, skus) {
    try {
      const cartRes = await fetch(`${window.Shopify.routes.root}cart.js`);
      const cart = await cartRes.json();
      const existingNote = (cart.note || '').trim();

      const handle = this.dataset.productHandle || '';
      const header = handle
        ? `Bundle-Auswahl [${handle}]`
        : 'Bundle-Auswahl';

      const lines = [`${header}:`];
      lines.push(`  Sorten: ${titles.join(', ')}`);
      if (skus.length) lines.push(`  SKUs: ${skus.join(', ')}`);
      const bundleBlock = lines.join('\n');

      // Dedup: if we already wrote this block (e.g. double-click), don't repeat
      if (existingNote.includes(bundleBlock)) return;

      const combinedNote = existingNote
        ? `${bundleBlock}\n\n${existingNote}`
        : bundleBlock;

      await fetch(`${window.Shopify.routes.root}cart/update.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ note: combinedNote }),
      });
      console.log('[BundleConfigurator] Cart note updated');
    } catch (err) {
      // Non-fatal — the bundle is already in the cart, just no note.
      console.warn('[BundleConfigurator] Failed to set cart note:', err);
    }
  }

  /**
   * Tries several strategies to open the cart drawer after adding.
   * Works on Hyper (CartDrawer), Dawn (cart-drawer), Pipeline (quick-cart).
   */
  openCartDrawer() {
    const candidates = [
      '[aria-controls="CartDrawer"]',
      '.cart-drawer-button',
      '#cart-icon-bubble',
      '.js-cart-toggle',
      '[data-cart-drawer-toggle]',
    ];
    for (const sel of candidates) {
      const btn = document.querySelector(sel);
      if (btn) {
        btn.click();
        return;
      }
    }
    // Last fallback: redirect to /cart
    setTimeout(() => (window.location.href = '/cart'), 400);
  }
}

if (!customElements.get('bundle-configurator')) {
  customElements.define('bundle-configurator', BundleConfigurator);
}